import { ClipTypeEnum, Engine, EngineOptions, LottieClip, Subtitles, SubtitlesClip, TextBlock } from "@rendley/sdk";
import { ApplicationStore } from "../store/ApplicationStore";
import { RendleyStore } from "../store/RendleyStore";
import {CLIP_LIBRETTO_CLIP_ID, GenerateLibrettoClipId} from "../Constants";

export class RendleyService {
  private static get engine() {
    return Engine.getInstance();
  }

  static init(options: EngineOptions) {
    return this.engine.init(options);
  }

  static getEngine() {
    return this.engine;
  }

  static getCurrentTime() {
    return RendleyService.engine.getTimeline().currentTime;
  }

  static getFitDuration() {
    return RendleyService.engine.getTimeline().getFitDuration();
  }

  static getFps() {
    return RendleyService.engine.getTimeline().fps;
  }

  static play() {
    return RendleyService.engine.play();
  }

  static pause() {
    return RendleyService.engine.pause();
  }

  static seek(time: number) {
    return RendleyService.engine.seek(time);
  }

  static setBackgroundColor(color: string) {
    return RendleyService.engine.getDisplay().setBackgroundColor(color);
  }

  static setResolution(width: number, height: number) {
    return RendleyService.engine.getDisplay().setResolution(width, height);
  }

  static getFonts() {
    return RendleyService.engine.getFontRegistry().fonts.map((font) => font.font);
  }

  static getMediaById(mediaId: string) {
    return RendleyService.engine.getLibrary().getMediaById(mediaId);
  }

  static addMediaToGallery(file: File | string, mimeType?: string) {
    return RendleyService.engine.getLibrary().addMedia(file, mimeType);
  }

  static getClipById(clipId: string) {
    return RendleyService.engine.getClipById(clipId);
  }

  static getLayerById(layerId: string) {
    return RendleyService.engine.getTimeline().getLayerById(layerId);
  }

  static createLayer(index?: number) {
    const setAsScriptLayer = RendleyStore.getNumLayers() === 0 ? true : false;
    const layer = RendleyService.engine.getTimeline().createLayer({ index });
    if (setAsScriptLayer) {
      RendleyStore.setScriptLayerId(layer.id)
    }
    return layer;
  }

  static deleteLayer(layerId: string) {
    return RendleyService.engine.getTimeline().removeLayer(layerId);
  }

  static async moveClipToLayer(clipId: string, layerId: string) {
    return RendleyService.engine.getTimeline().moveClipToLayer(clipId, layerId);
  }

  static async addMediaToLayer(layerId: string, mediaId: string, dropTime = RendleyService.getCurrentTime()) {
    const layer = RendleyService.getLayerById(layerId);

    if (layer == null) {
      return null;
    }

    return layer.addClip({
      mediaDataId: mediaId,
      startTime: dropTime,
    });
  }

  static addTextClipToLayer(layerId: string, text: string = "Hello World") {
    const layer = RendleyService.getLayerById(layerId);
    const currentTime = RendleyService.getCurrentTime();

    if (layer == null) {
      return null;
    }

    return layer.addClip({
      text,
      startTime: currentTime,
      duration: 1,
      style: {
        color: "#FFFFFF",
      },
    });
  }

  static deleteMediaById(mediaId: string) {
    return RendleyService.engine.getLibrary().deleteMedia(mediaId);
  }

  static deleteClipById(clipId: string) {
    return RendleyService.engine.getTimeline().removeClip(clipId);
  }

  static deleteTransitionById(transitionId: string, layerId: string) {
    const layer = RendleyService.getLayerById(layerId);
    return layer?.removeTransition(transitionId);
  }

  static deleteLibrettoClipAtCurrentTime() {
    const selectedLibrettoClipId = ApplicationStore.selectedLibrettoClipId;

    if (selectedLibrettoClipId != null) {
      return RendleyService.deleteLibrettoClipById(selectedLibrettoClipId);
    }

    const selectedTransitionId = ApplicationStore.selectedTransitionId;

    if (selectedTransitionId != null) {
      const layerId = RendleyStore.getLayerFromTransitionId(selectedTransitionId)?.id;

      if (layerId != null) {
        return RendleyService.deleteTransitionById(selectedTransitionId, layerId);
      }
    }
  }

  // Not working. Need to rewrite.
  static async cutSegmentFromLibrettoClip(start: number, end: number) {
    const selectedLibrettoClipId = ApplicationStore.selectedLibrettoClipId;
    
    // Clear the selected range first to prevent UI errors
    ApplicationStore.setSelectedRange(null);
    ApplicationStore.setRangeToolEnabled(false);
    
    const middleLibrettoClipId = GenerateLibrettoClipId();
    await RendleyService.splitLibrettoClipAtTime(ApplicationStore.selectedLibrettoClipId, start, middleLibrettoClipId);

    const rightLibrettoClipId = GenerateLibrettoClipId();
    await RendleyService.splitLibrettoClipAtTime(middleLibrettoClipId, end, rightLibrettoClipId);

    // Delete the middle clip
    RendleyService.deleteLibrettoClipById(middleLibrettoClipId);

    // Adjust clip layout to handle any overlaps
    Engine.getInstance().getTimeline().adjustClipsLayout();
  }

  static deleteLibrettoClipById(librettoClipId: string) {
    try {
      // Step 1: Get all associated data BEFORE any deletions
      const clipIdsList = RendleyStore.getClipIdsForLibrettoClip(librettoClipId);
      const layerId = RendleyStore.getLayerIdForLibrettoClip(librettoClipId);

      if (clipIdsList.length === 0) {
        console.warn(`No clips found for LibrettoClip: ${librettoClipId}`);
        return false;
      }

      console.log(`[Delete] LibrettoClip ${librettoClipId}: ${clipIdsList.length} clips on layer ${layerId}`);

      // Step 2: Remove from rendering IMMEDIATELY (atomic visual removal)
      if (layerId) {
        RendleyStore.removeLibrettoClipIdFromLayer(librettoClipId, layerId);
      }

      // Clear LibrettoClip mappings
      clipIdsList.forEach(clipId => {
        RendleyStore.removeClipIdForLibrettoClip(librettoClipId, clipId);
      });
      RendleyStore.removeLayerIdForLibrettoClip(librettoClipId);

      // Clear selection if this clip was selected
      if (ApplicationStore.selectedLibrettoClipId === librettoClipId) {
        ApplicationStore.setSelectedLibrettoClipIdToNull();
      }

      // Step 3: Delete actual Rendley clips from engine
      let allDeleted = true;
      for (const clipId of clipIdsList) {
        const deleted = RendleyService.deleteClipById(clipId);
        allDeleted = allDeleted && deleted;

        if (deleted) {
          // Remove from RendleyStore.clips as well
          RendleyStore.deleteClip(clipId, layerId);
        }
      }

      // Step 4: Trigger layout adjustment
      Engine.getInstance().getTimeline().adjustClipsLayout();

      console.log(`[Delete] LibrettoClip ${librettoClipId} deletion ${allDeleted ? 'successful' : 'partial'}`);
      return allDeleted;

    } catch (error) {
      console.error(`[Delete] Failed to delete LibrettoClip ${librettoClipId}:`, error);
      return false;
    }
  }


  static getClipsByTime(timeInSeconds: number): Record<string, string[]> {
    const timeline = Engine.getInstance().getTimeline();

    const result: Record<string, string[]> = {};

    for (const layerId of timeline.layersOrder) {
      const layer = timeline.getLayerById(layerId);

      if (layer == null) {
        continue;
      }

      for (const clipId of layer.clipsIds) {
        const clip = layer.getClipById(clipId);

        if (clip == null) {
          continue;
        }

        if (clip.getLeftBound() <= timeInSeconds && clip.getRightBound() >= timeInSeconds) {
          result[layerId] = [...(result[layerId] ?? []), clipId];
        }
      }
    }

    return result;
  }

  static splitLibrettoClipAtCurrentTime = async () => {
    await RendleyService.splitLibrettoClipAtTime(ApplicationStore.selectedLibrettoClipId, RendleyStore.currentTime, GenerateLibrettoClipId());
  }

  static splitLibrettoClipAtTime = async (librettoClipId: string, time: number, newLibrettoClipId: string) => {
    const selectedLibrettoClipId = librettoClipId;
    const currentTime = time;
    const fitDuration = Engine.getInstance().getTimeline().getFitDuration();

    if (currentTime <= 0 || currentTime >= fitDuration) {
      return;
    }

    if (selectedLibrettoClipId == null) {
      // May need to handle case here. (Cut all the clips the playhead is on)
      return;
    }

    const clipIdsForLibrettoClip = RendleyStore.getClipIdsForLibrettoClip(selectedLibrettoClipId);
    const librettoClipLayerId = RendleyStore.getLayerIdForLibrettoClip(selectedLibrettoClipId);
    const clipsAtCurrentTime = RendleyService.getClipsByTime(currentTime);

    let rendleyClipId = null;
    if (clipsAtCurrentTime) {
      const clipsOnLayer =  [librettoClipLayerId];

      if (!clipsOnLayer || clipsOnLayer.length === 0) {
        console.log("Clips on layer not found")
      }

      rendleyClipId = clipsOnLayer.find(clipId => clipIdsForLibrettoClip.includes(clipId));
    }

    const oldRendleyclip = RendleyStore.clips[rendleyClipId];
    if (oldRendleyclip == null) {
      return;
    }
    if (oldRendleyclip?.type === ClipTypeEnum.SUBTITLES) {
      return;
    }

    const layerId = Object.values(RendleyStore.layers).find((layer) => layer.clipsIds.includes(rendleyClipId))?.id;

    if (layerId == null) {
      return;
    }
    const layer = this.engine.getTimeline().getLayerById(layerId);
    if (layer == null) {
      return;
    }

    const newRendleyClip = await layer.splitClip(oldRendleyclip.id, currentTime);

    RendleyStore.removeLibrettoClipIdFromLayer(selectedLibrettoClipId, layerId)

    // This above clip and all clips that start after currentTime are a part of the new LibrettoClip
    // that is created because of the split.
    newRendleyClip.setCustomData(CLIP_LIBRETTO_CLIP_ID, newLibrettoClipId);
    RendleyStore.addClipIdForLibrettoClip(newLibrettoClipId, newRendleyClip.id);
    RendleyStore.addLibrettoClipIdToLayer(newLibrettoClipId, layer.id);
    RendleyStore.setLayerIdForLibrettoClip(newLibrettoClipId, layer.id);

    for (const clipId of clipIdsForLibrettoClip) {
      const clip = Engine.getInstance().getTimeline().getClipById(clipId);
      if (clip == null) {
        continue;
      }
      if (clip.getLeftBound() > currentTime) {
        clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, newLibrettoClipId);
        RendleyStore.addClipIdForLibrettoClip(newLibrettoClipId, clip.id);
      }
    }

    RendleyStore.addLibrettoClipIdToLayer(selectedLibrettoClipId, layerId);
  }

  static async duplicateLibrettoClipById(librettoClipId: string) {


  }

  static async duplicateClipById(clipId: string) {
    const timeline = Engine.getInstance().getTimeline();
    const clip = timeline.getClipById(clipId);

    if (clip == null) {
      return;
    }

    const layer = timeline.getLayerByClipId(clipId);

    if (layer == null) {
      return;
    }

    const newClip = clip.clone();

    if (newClip == null) {
      return;
    }

    // duplicate clip
    newClip.generateNewId();
    newClip.setStartTime(clip.getRightBound());

    const isOverlapped = layer.clipsIds.some((clipId) => {
      const searchClip = layer.getClipById(clipId);

      if (searchClip == null || searchClip.getId() === newClip.getId()) {
        return false;
      }

      return newClip.getLeftBound() < searchClip.getRightBound() && newClip.getRightBound() > searchClip.getLeftBound();
    });

    if (isOverlapped) {
      const newLayer = timeline.createLayer();
      newClip.setStartTime(clip.getStartTime());
      await newLayer.addClip(newClip);
    } else {
      await layer.addClip(newClip);
    }
  }

  static moveClipLeft = () => {

  };

  static export() {
    return RendleyService.engine.export();
  }

  static async importSubtitlesSrt(srtContent: string) {
    const subtitles = Engine.getInstance().getSubtitlesManager().convertSRTToSubtitles(srtContent);

    return await RendleyService.addSubtitles(subtitles);
  }

  static async addManualSubtitles(textBlocks?: TextBlock[]) {
    const subtitles = new Subtitles({
      textBlocks: textBlocks ?? [
        {
          text: "Sample",
          time: 0,
          duration: 1,
        },
      ],
    });

    return await RendleyService.addSubtitles(subtitles);
  }

  static async addSubtitles(subtitles: Subtitles) {
    Engine.getInstance().getUndoManager().startGroup("Add subtitles");
    const subtitlesId = Engine.getInstance().getLibrary().addSubtitles(subtitles);

    const layer = RendleyService.createLayer();

    const subtitlesClip = new SubtitlesClip({
      subtitlesId: subtitlesId,
    });

    await layer.addClip(subtitlesClip);
    Engine.getInstance().getUndoManager().endGroup();

    return subtitlesClip.id;
  }

  static async createLottieClip(options: {
    dataUrl: string;
    propertiesUrl: string;
    startTime: number;
    layerId?: string;
  }) {
    Engine.getInstance().getUndoManager().startGroup("Add text to timeline");
    const layer = options.layerId ? RendleyService.getLayerById(options.layerId) : RendleyService.createLayer();

    if (layer == null) {
      Engine.getInstance().getUndoManager().cancelGroup();
      return null;
    }

    const lottieClip = new LottieClip({
      dataUrl: options.dataUrl,
      propertiesUrl: options.propertiesUrl,
      startTime: options.startTime,
    });

    await layer.addClip(lottieClip);
    Engine.getInstance().getUndoManager().endGroup();

    return lottieClip;
  }

  static async createTextClip(options: { text: string; startTime: number; layerId?: string }) {
    Engine.getInstance().getUndoManager().startGroup("Add text to timeline");
    const layer = options.layerId ? RendleyService.getLayerById(options.layerId) : RendleyService.createLayer();

    if (layer == null) {
      Engine.getInstance().getUndoManager().cancelGroup();
      console.log("cannot create layer");
      return null;
    }

    const clip = await layer.addClip({
      type: "text",
      text: options.text,
      startTime: options.startTime,
      style: {
        padding: [40, 20],
      },
    });
    Engine.getInstance().getUndoManager().endGroup();

    return clip;
  }

  static async replaceMedia(mediaDataId: string, file: File | Uint8Array) {
    return RendleyService.getEngine().getLibrary().replaceMedia(mediaDataId, file);
  }

  static loadFont(fontName: string, url: string) {
    return Engine.getInstance().getFontRegistry().loadFromCssUrl(fontName, url);
  }

  static pauseUndo() {
    return Engine.getInstance().getUndoManager().pushPause();
  }

  static resumeUndo() {
    return Engine.getInstance().getUndoManager().popPause();
  }

  static getTransitionById(transitionId: string) {
    const layers = Engine.getInstance().getTimeline().layers;

    for (const layerId in layers) {
      const layer = layers[layerId];

      for (const transition of layer.transitions) {
        if (transitionId === transition.getId()) {
          return transition;
        }
      }
    }

    return null;
  }
}
